import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { PlusIcon, PencilSimpleIcon, GearIcon, TrashIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Grid from '@/components/inputs/CustomGrid';
import useLoading from '@/components/loading/hooks/useLoading';
import useSnackbar from '@/components/dialogs/snackbars/hooks/useSnackbar';
import useConfirm from '@/components/dialogs/hooks/useConfirm';
import Connections, { ApiEndpoints } from '@/components/connections/Connections';

const iconBtn = 'inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer';

const CompanyList = () => {
  const [companies, setCompanies] = useState([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, Loading, setLoading } = useLoading(true);
  const { createSuccessSnackbar, createErrorSnackbar } = useSnackbar();
  const { openConfirm, ConfirmDialog } = useConfirm();

  const loadCompanies = async () => {
    const res = await Connections.postRequest(ApiEndpoints.companiesAll);
    if (res.ok) {
      setCompanies(res.data.companies || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadCompanies().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async companyId => {
    const confirmed = await openConfirm(t('company.deleteConfirm'));
    if (!confirmed) return;

    setLoading(true);
    const res = await Connections.postRequest(ApiEndpoints.companyDelete, { companyId });
    if (res.ok) {
      createSuccessSnackbar(t('company.deleted'));
      await loadCompanies();
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setLoading(false);
  };

  if (loading) return <Loading />;

  return (
    <>
      <Helmet>
        <title>{t('app.title')} | {t('companies')}</title>
      </Helmet>
      <ConfirmDialog />
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-semibold">{t('companies')}</h1>
        <button
          type="button"
          onClick={() => navigate('/companies/new')}
          title={t('company.create')}
          className="inline-flex items-center justify-center h-9 w-9 rounded-md text-primary hover:bg-muted transition-colors cursor-pointer"
        >
          <PlusIcon size={22} />
        </button>
      </div>
      {companies.length === 0 ? (
        <p className="text-muted-foreground">{t('company.empty')}</p>
      ) : (
        <Grid container spacing={3}>
          {companies.map(company => (
            <Grid item xs={12} md={6} lg={4} key={company._id}>
              <Card
                className="p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/companies/${company._id}`)}
              >
                <div className="flex justify-between items-start mb-2">
                  <h2 className="text-lg font-semibold">{company.name}</h2>
                  <Badge
                    variant={company.status === 'active' ? 'secondary' : 'outline'}
                    style={{ borderRadius: '0.375rem' }}
                  >
                    {company.status}
                  </Badge>
                </div>
                {company.eik && (
                  <p className="text-sm text-muted-foreground">{t('eik')}: {company.eik}</p>
                )}
                {company.vatId && (
                  <p className="text-sm text-muted-foreground">{t('vatId')}: {company.vatId}</p>
                )}
                {company.email && (
                  <p className="text-sm text-muted-foreground mt-1">{company.email}</p>
                )}
                <div className="flex justify-end mt-4 gap-1">
                  <button
                    type="button"
                    className={iconBtn}
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/companies/${company._id}?tab=info`);
                    }}
                    title={t('edit')}
                  >
                    <PencilSimpleIcon size={18} />
                  </button>
                  <button
                    type="button"
                    className={iconBtn}
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/companies/${company._id}?tab=settings`);
                    }}
                    title={t('settings')}
                  >
                    <GearIcon size={18} />
                  </button>
                  <button
                    type="button"
                    className={`${iconBtn} hover:text-destructive`}
                    onClick={e => {
                      e.stopPropagation();
                      handleDelete(company._id);
                    }}
                    title={t('delete')}
                  >
                    <TrashIcon size={18} />
                  </button>
                </div>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};

export default CompanyList;
